// The genome readout for the selected animal.
//
// A bare column of numbers cannot answer the only question anyone actually asks
// of an individual — "is this one unusual?" — because the answer depends on what
// its species is allowed to be. So every gene is drawn as a BAR showing where
// this individual sits inside its own species' range (species.ts), with the
// decoded value in real units beside it. A wolf two-thirds of the way along its
// mass bar reads as a big wolf at a glance; 41 kg does not.
//
// Data is a plain snapshot posted by the sim worker (the agent itself lives in
// the worker), refreshed a few times per second. main.ts calls this every frame,
// so the DOM is built ONCE per selected animal and only the numbers are written
// afterwards.

import { DEER, WOLF, type Species } from "../sim/species";
import { REF_MASS_KG, SIM_SECONDS_PER_YEAR } from "../sim/scale";
import type { TraitKey } from "../sim/traits";

export interface InspectData {
  id: number;
  species: string;
  sex: "m" | "f";
  age: number;
  lifespan: number;
  energy: number;
  maxEnergy: number;
  size: number;
  massKg: number;
  brainMass: number;
  maxSpeed: number;
  senseRadius: number;
  metabolicRate: number;
  reproThreshold: number;
  maturityAge: number;
  canBreed: boolean;

  // --- Optional: two body genes the worker does not send yet ----------------
  // Both are real genes in traits.ts, so the readout is incomplete without
  // them. They render only when present; see the report in simWorker.ts's
  // `inspectData()` for where they would come from (`ph.maxForce`,
  // `ph.gestationCost`).
  maxForce?: number;
  gestationCost?: number;
}

// --- Gene table --------------------------------------------------------------
// `raw` must return the value in the SAME units as the species range in
// species.ts, so it can be normalised against it. `show` is what the reader
// sees, in whatever unit is most human.

interface GeneRow {
  key: TraitKey;
  label: string;
  /** Value in species-range units, or undefined if the worker didn't send it. */
  raw: (a: InspectData) => number | undefined;
  /** Human-readable value. */
  show: (raw: number, a: InspectData) => string;
  /** Human-readable range endpoint, for the hover title. */
  bound: (v: number) => string;
}

const yr = (s: number): string => `${(s / SIM_SECONDS_PER_YEAR).toFixed(1)} yr`;
const kmh = (mps: number): string => `${(mps * 3.6).toFixed(0)} km/h`;
const kgs = (size: number): string => `${(size * REF_MASS_KG).toFixed(0)} kg`;

const GENES: GeneRow[] = [
  {
    key: "mass",
    label: "Body mass",
    raw: (a) => a.size,
    show: (_r, a) => `${a.massKg.toFixed(0)} kg`,
    bound: kgs,
  },
  {
    key: "maxSpeed",
    label: "Top speed",
    raw: (a) => a.maxSpeed,
    show: (r) => kmh(r),
    bound: kmh,
  },
  {
    key: "maxForce",
    label: "Agility",
    raw: (a) => a.maxForce,
    show: (r) => r.toFixed(0),
    bound: (v) => v.toFixed(0),
  },
  {
    key: "senseRadius",
    label: "Senses",
    raw: (a) => a.senseRadius,
    show: (r) => `${r.toFixed(0)} m`,
    bound: (v) => `${v.toFixed(0)} m`,
  },
  {
    key: "metabolicRate",
    label: "Metabolism",
    raw: (a) => a.metabolicRate,
    show: (r) => `×${r.toFixed(2)}`,
    bound: (v) => `×${v.toFixed(2)}`,
  },
  {
    // The gene is reserve density; the tank is that × body scale, so divide the
    // decoded tank back out to place it inside the gene's range.
    key: "maxEnergy",
    label: "Fat reserves",
    raw: (a) => (a.size > 0 ? a.maxEnergy / a.size : undefined),
    show: (_r, a) => `${a.maxEnergy.toFixed(0)} E`,
    bound: (v) => `${v.toFixed(0)}/scale`,
  },
  {
    key: "reproThreshold",
    label: "Breeds above",
    raw: (a) => a.reproThreshold,
    show: (r) => `${(r * 100).toFixed(0)}%`,
    bound: (v) => `${(v * 100).toFixed(0)}%`,
  },
  {
    key: "gestationCost",
    label: "Invests in young",
    raw: (a) => a.gestationCost,
    show: (r) => `${(r * 100).toFixed(0)}%`,
    bound: (v) => `${(v * 100).toFixed(0)}%`,
  },
  {
    key: "maturityAge",
    label: "Matures at",
    raw: (a) => a.maturityAge,
    show: (r) => yr(r),
    bound: yr,
  },
  {
    key: "lifespan",
    label: "Lifespan",
    raw: (a) => a.lifespan,
    show: (r) => yr(r),
    bound: yr,
  },
];

/** What each gene buys and costs — the two-sided ledger, in a hover title. */
const LEDGER: Partial<Record<TraitKey | "brain", string>> = {
  mass: "Buys predation resistance, intake and fasting endurance. Costs locomotion.",
  maxSpeed: "Buys escape and pursuit. Costs sprint muscle at rest — and above a walk you cannot graze.",
  maxForce: "Buys agility in a chase. Costs neuromuscular tissue at rest.",
  senseRadius: "Buys seeing food, mates and danger sooner. Costs sense organs and the brain to process them.",
  metabolicRate: "Buys grazing throughput and faster digestion. Costs a higher resting burn.",
  maxEnergy: "Buys a buffer against lean spells. Costs fat to build and haul.",
  reproThreshold: "Buys offspring born in better condition. Costs having fewer of them.",
  gestationCost: "Buys better-provisioned young. Drawn straight from the mother's reserves.",
  maturityAge: "Buys a cheaper childhood. Costs years before it can breed at all.",
  lifespan: "Buys more breeding seasons. Costs continuous self-repair.",
  brain: "Buys reacting to what the senses report. Neural tissue is the most expensive thing an animal runs.",
};

function speciesOf(name: string): Species {
  const n = name.toLowerCase();
  if (n === WOLF.key || n === WOLF.name.toLowerCase()) return WOLF;
  return DEER;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

function lifeStage(a: InspectData): string {
  if (a.age < a.maturityAge) return "juvenile";
  if (a.age > a.lifespan * 0.7) return "senescent";
  return "prime";
}

// --- Mounted view ------------------------------------------------------------
// Rebuilt only when the selected animal changes; otherwise the same nodes are
// rewritten, and only where the text actually differs.

interface Bar {
  fill: HTMLElement;
  val: HTMLElement;
  last: string;
  lastW: number;
}

interface Mounted {
  host: HTMLElement;
  id: number;
  species: string;
  root: HTMLElement;
  sub: HTMLElement;
  energy: Bar;
  age: Bar;
  ageMark: HTMLElement;
  breed: HTMLElement;
  genes: { row: GeneRow; bar: Bar; sp: Species }[];
  brain: Bar;
}

let mounted: Mounted | null = null;
let emptyHost: HTMLElement | null = null;

function el(tag: string, cls?: string, text?: string): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/** label | track | value — the one layout the whole panel is built from. */
function meter(
  label: string,
  cls: string,
  title?: string,
): { row: HTMLElement; track: HTMLElement; bar: Bar } {
  const row = el("div", `insp-row ${cls}`);
  if (title) row.title = title;
  row.appendChild(el("div", "insp-k", label));
  const track = el("div", "insp-track");
  const fill = el("i", "insp-fill");
  track.appendChild(fill);
  track.appendChild(el("b", "insp-mid"));
  row.appendChild(track);
  const val = el("div", "insp-v", "—");
  row.appendChild(val);
  return { row, track, bar: { fill, val, last: "", lastW: -1 } };
}

function setBar(b: Bar, frac: number, text: string): void {
  const w = Math.round(clamp01(frac) * 1000) / 10;
  if (w !== b.lastW) {
    b.fill.style.width = `${w}%`;
    b.lastW = w;
  }
  if (text !== b.last) {
    b.val.textContent = text;
    b.last = text;
  }
}

function renderEmpty(host: HTMLElement): void {
  host.innerHTML = "";
  const box = el("div", "insp insp-empty");
  box.appendChild(el("div", "insp-eyebrow", "Inspector"));
  box.appendChild(el("div", "insp-mark", "◎"));
  const p = el("p", "insp-lead");
  p.appendChild(el("strong", undefined, "Pick an animal."));
  p.appendChild(document.createTextNode(" Tap any deer or wolf on the map to open its genome."));
  box.appendChild(p);
  box.appendChild(
    el(
      "p",
      "insp-note",
      "Every gene is drawn as a bar showing where that individual sits inside the range its species allows — so an unusually large wolf, or an unusually blind deer, reads at a glance.",
    ),
  );
  const legend = el("div", "insp-legend");
  const deer = el("span", "insp-key");
  deer.appendChild(el("i", "insp-swatch is-deer"));
  deer.appendChild(document.createTextNode("Deer"));
  const wolf = el("span", "insp-key");
  wolf.appendChild(el("i", "insp-swatch is-wolf"));
  wolf.appendChild(document.createTextNode("Wolf"));
  legend.appendChild(deer);
  legend.appendChild(wolf);
  box.appendChild(legend);
  host.appendChild(box);
}

function build(host: HTMLElement, a: InspectData): Mounted {
  const sp = speciesOf(a.species);
  host.innerHTML = "";

  const root = el("div", "insp");
  root.dataset.sp = sp.key;
  root.appendChild(el("div", "insp-eyebrow", "Inspector"));

  // Identity ----------------------------------------------------------------
  const head = el("div", "insp-head");
  head.appendChild(el("span", "insp-dot"));
  const idt = el("div", "insp-idt");
  const title = el("div", "insp-title");
  title.appendChild(el("span", "insp-name", sp.name));
  title.appendChild(el("span", "insp-id", `#${a.id}`));
  idt.appendChild(title);
  const sub = el("div", "insp-sub");
  idt.appendChild(sub);
  head.appendChild(idt);
  root.appendChild(head);

  // Condition ---------------------------------------------------------------
  root.appendChild(el("h4", "insp-h", "Condition"));
  const energy = meter("Energy", "is-energy", "Current reserves against this animal's own tank.");
  root.appendChild(energy.row);
  const age = meter("Age", "is-age", "Age against its own lifespan. The notch marks when it reaches maturity.");
  const ageMark = el("b", "insp-mark-tick");
  age.track.appendChild(ageMark);
  root.appendChild(age.row);
  const pills = el("div", "insp-pills");
  const breed = el("span", "insp-pill");
  pills.appendChild(breed);
  root.appendChild(pills);

  // Genome ------------------------------------------------------------------
  const h = el("h4", "insp-h", "Genome");
  h.appendChild(el("span", "insp-hint", `within ${sp.name.toLowerCase()} range`));
  root.appendChild(h);

  const genes: Mounted["genes"] = [];
  for (const row of GENES) {
    if (row.raw(a) === undefined) continue; // field not sent by the worker
    const [lo, hi] = sp.traits[row.key];
    const ledger = LEDGER[row.key] ?? "";
    const m = meter(row.label, "is-gene", `${sp.name}: ${row.bound(lo)} – ${row.bound(hi)}\n${ledger}`);
    root.appendChild(m.row);
    genes.push({ row, bar: m.bar, sp });
  }
  const brain = meter("Brain size", "is-gene is-brain", `Share of the largest brain this species can grow.\n${LEDGER.brain}`);
  root.appendChild(brain.row);

  root.appendChild(
    el("div", "insp-foot", "Bars are normalised to the species' allowed range; the notch is its midpoint."),
  );

  host.appendChild(root);
  return {
    host,
    id: a.id,
    species: sp.key,
    root,
    sub,
    energy: energy.bar,
    age: age.bar,
    ageMark,
    breed,
    genes,
    brain: brain.bar,
  };
}

function refresh(m: Mounted, a: InspectData): void {
  const sub = `${a.sex === "f" ? "female" : "male"} · ${lifeStage(a)} · ${speciesOf(a.species).binomial}`;
  if (m.sub.textContent !== sub) m.sub.textContent = sub;

  const efrac = a.maxEnergy > 0 ? a.energy / a.maxEnergy : 0;
  setBar(m.energy, efrac, `${a.energy.toFixed(0)} / ${a.maxEnergy.toFixed(0)}`);
  const level = efrac < 0.15 ? "lo" : efrac < 0.35 ? "mid" : "ok";
  if (m.energy.fill.dataset.level !== level) m.energy.fill.dataset.level = level;

  setBar(m.age, a.lifespan > 0 ? a.age / a.lifespan : 0, yr(a.age));
  const mark = a.lifespan > 0 ? clamp01(a.maturityAge / a.lifespan) * 100 : 0;
  m.ageMark.style.left = `${mark.toFixed(1)}%`;

  const canBreed = a.canBreed;
  const pill = canBreed ? "ready to breed" : a.age < a.maturityAge ? "not yet mature" : "not in condition";
  if (m.breed.textContent !== pill) {
    m.breed.textContent = pill;
    m.breed.classList.toggle("is-on", canBreed);
  }

  for (const g of m.genes) {
    const raw = g.row.raw(a);
    if (raw === undefined) continue;
    const [lo, hi] = g.sp.traits[g.row.key];
    const n = hi > lo ? (raw - lo) / (hi - lo) : 0;
    setBar(g.bar, n, g.row.show(raw, a));
  }
  setBar(m.brain, a.brainMass, `${(a.brainMass * 100).toFixed(0)}%`);
}

export function updateInspector(host: HTMLElement, a: InspectData | null): void {
  injectStyle();
  if (!a) {
    if (emptyHost !== host) {
      renderEmpty(host);
      emptyHost = host;
      mounted = null;
    }
    return;
  }
  const changed =
    !mounted ||
    mounted.host !== host ||
    mounted.id !== a.id ||
    mounted.species !== speciesOf(a.species).key;
  if (changed) {
    mounted = build(host, a);
    emptyHost = null;
  }
  refresh(mounted!, a);
}

// --- Styles ------------------------------------------------------------------
// Injected here so the module owns the markup AND its presentation and the two
// cannot drift. Only `.insp-*` classes are defined. Colours come from the design
// tokens in style.css, with literal fallbacks so it still reads standalone.

const STYLE_ID = "eco-inspector-style";
let styled = false;

function injectStyle(): void {
  if (styled) return;
  styled = true;
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
.insp {
  --sp: var(--accent, #6fbf5c);
  /* Consume the global type roles so the specimen card matches the housing. */
  --insp-sans: var(--font-ui, ui-sans-serif, system-ui, sans-serif);
  --insp-mono: var(--font-data, ui-monospace, SFMono-Regular, Consolas, monospace);
  --insp-label: var(--font-label, "Arial Narrow", system-ui, sans-serif);
  --insp-prose: var(--font-prose, Georgia, serif);
  font-family: var(--insp-sans);
  color: var(--ink, #dde5d4);
}
.insp[data-sp="deer"] { --sp: var(--deer, #d8ab6a); }
.insp[data-sp="wolf"] { --sp: var(--wolf, #93a6ba); }

.insp-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 9px;
  border: 1px solid var(--line, #2a3327);
  border-left: 3px solid var(--sp);
  border-radius: var(--r-md, 10px);
  background: var(--panel-2, #1a2017);
}
.insp-dot {
  flex: none;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: var(--sp);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--sp) 22%, transparent);
}
.insp-idt { min-width: 0; }
.insp-title { display: flex; align-items: baseline; gap: 6px; }
.insp-name {
  font-family: var(--insp-label);
  font-size: 19px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.15em;
  line-height: 1.05;
}
.insp-id { font-family: var(--insp-mono); font-size: 11px; color: var(--sp); opacity: .85; }
.insp-sub {
  font-family: var(--insp-prose);
  font-size: 12.5px;
  color: var(--muted, #8a9580);
  font-style: italic;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.insp-h {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin: 12px 0 5px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--muted, #8a9580);
}
.insp-hint {
  font-size: 9.5px;
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
  color: var(--dim, #5f6b57);
}

.insp-row {
  display: grid;
  grid-template-columns: minmax(58px, 82px) 1fr minmax(52px, auto);
  align-items: center;
  gap: 7px;
  padding: 2.5px 0;
}
.insp-k {
  font-size: 10.5px;
  color: var(--muted, #8a9580);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.insp-track {
  position: relative;
  height: 8px;
  border-radius: 4px;
  background: var(--panel-2, #1a2017);
  border: 1px solid var(--line, #2a3327);
  overflow: hidden;
}
.insp-fill {
  display: block;
  height: 100%;
  width: 0;
  border-radius: 3px;
  background: var(--sp);
  transition: width 0.18s ease-out;
}
.insp-mid {
  position: absolute;
  top: 1px;
  bottom: 1px;
  left: 50%;
  width: 1px;
  background: var(--bg, #0b0f0a);
  opacity: 0.75;
}
.insp-mark-tick {
  position: absolute;
  top: -1px;
  bottom: -1px;
  width: 2px;
  margin-left: -1px;
  background: var(--ink, #dde5d4);
  opacity: 0.55;
}
.insp-v {
  font-family: var(--insp-mono);
  font-size: 10.5px;
  text-align: right;
  color: var(--ink, #dde5d4);
  white-space: nowrap;
}

/* Condition reads in the ANIMAL's colour, not green: green means vegetation
   everywhere else in this interface, and an energy bar is not vegetation. A
   healthy deer's reserves are deer-coloured; they go amber then red as it
   starves, which is the only place warning colour is earned. */
.insp-row.is-energy .insp-fill { background: var(--sp); }
.insp-row.is-energy .insp-fill[data-level="mid"] { background: var(--warn, #e0a458); }
.insp-row.is-energy .insp-fill[data-level="lo"] { background: var(--danger, #d4675a); }
.insp-row.is-energy .insp-mid,
.insp-row.is-age .insp-mid { display: none; }
.insp-row.is-age .insp-fill { background: var(--muted, #8a9580); }
.insp-row.is-brain .insp-fill { background: color-mix(in srgb, var(--sp) 65%, var(--ink, #dde5d4)); }

.insp-pills { display: flex; flex-wrap: wrap; gap: 4px; margin: 5px 0 0; }
.insp-pill {
  padding: 1.5px 7px;
  border-radius: 999px;
  border: 1px solid var(--line, #2a3327);
  background: var(--panel-2, #1a2017);
  color: var(--dim, #5f6b57);
  font-size: 9.5px;
  letter-spacing: 0.02em;
}
.insp-pill.is-on {
  border-color: color-mix(in srgb, var(--accent, #6fbf5c) 55%, transparent);
  color: var(--accent, #6fbf5c);
}

.insp-foot {
  margin-top: 8px;
  font-size: 9.5px;
  line-height: 1.4;
  color: var(--dim, #5f6b57);
}

/* ---- Empty state ---- */
.insp-empty { text-align: left; padding: 4px 2px; }
.insp-eyebrow {
  margin: 0 0 6px;
  font-family: var(--insp-label);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--muted, #8a9580);
}
.insp-mark {
  margin: 10px 0 6px;
  font-size: 26px;
  line-height: 1;
  color: var(--accent, #6fbf5c);
  opacity: 0.7;
}
.insp-lead { margin: 0 0 6px; font-size: 12px; line-height: 1.45; }
.insp-note { margin: 0; font-size: 10.5px; line-height: 1.45; color: var(--muted, #8a9580); }
.insp-legend { display: flex; gap: 14px; margin-top: 10px; font-size: 10.5px; color: var(--muted, #8a9580); }
.insp-key { display: inline-flex; align-items: center; gap: 5px; }
.insp-swatch { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.insp-swatch.is-deer { background: var(--deer, #d8ab6a); }
.insp-swatch.is-wolf { background: var(--wolf, #93a6ba); }
`;
  document.head.appendChild(s);
}
